import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Filter, X, Play, Image as ImageIcon, Video } from 'lucide-react';
import { VideoModal, VideoCard } from '@/components/VideoModal';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Photo Modal Component
const PhotoModal = ({ isOpen, onClose, photo, photos, currentIndex, onNavigate }) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl w-full p-0 bg-card border-border overflow-hidden" data-testid="photo-modal">
        <div className="relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-50 p-2 rounded-full bg-black/50 hover:bg-black/70 transition-colors"
            data-testid="photo-modal-close"
          >
            <X className="w-6 h-6 text-white" />
          </button>
          
          <div className="relative aspect-video bg-black flex items-center justify-center">
            {photo && (
              <img
                src={photo.image_url}
                alt={photo.title || "VR Experience"}
                className="max-w-full max-h-full object-contain"
              />
            )}
          </div>
          
          {/* Navigation */}
          <div className="flex justify-between items-center p-4 bg-card">
            <button
              onClick={() => onNavigate(-1)}
              disabled={currentIndex === 0}
              className="px-4 py-2 rounded-lg bg-muted hover:bg-muted/80 disabled:opacity-50 disabled:cursor-not-allowed text-white"
            >
              Previous
            </button>
            <span className="text-muted-foreground text-sm">
              {currentIndex + 1} / {photos.length}
            </span>
            <button
              onClick={() => onNavigate(1)}
              disabled={currentIndex === photos.length - 1}
              className="px-4 py-2 rounded-lg bg-muted hover:bg-muted/80 disabled:opacity-50 disabled:cursor-not-allowed text-white"
            >
              Next
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Photo Card Component
const PhotoCard = ({ photo, onClick, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.4, delay: index * 0.03 }}
    className="group cursor-pointer rounded-xl overflow-hidden glass-light"
    onClick={onClick}
    data-testid={`photo-card-${photo.id}`}
  >
    <div className="aspect-square relative overflow-hidden">
      <img
        src={photo.image_url}
        alt={photo.title || "VR Experience"}
        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          whileHover={{ opacity: 1, scale: 1 }}
          className="opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
            <ImageIcon className="w-6 h-6 text-white" />
          </div>
        </motion.div>
      </div>
    </div>
  </motion.div>
);

export default function MediaPage() {
  const [videos, setVideos] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [activeTab, setActiveTab] = useState('photos'); // 'videos' or 'photos'
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/videos`),
      axios.get(`${API}/gallery`)
    ])
      .then(([videosRes, photosRes]) => {
        setVideos(videosRes.data);
        setPhotos(photosRes.data);
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setLoading(false);
      });
  }, []);

  // Get unique categories for videos
  const videoCategories = ['all', ...new Set(videos.map(v => v.category))];

  // Filter videos by category
  const filteredVideos = selectedCategory === 'all' 
    ? videos 
    : videos.filter(v => v.category === selectedCategory);

  const handlePhotoClick = (photo, index) => {
    setSelectedPhoto(photo);
    setCurrentPhotoIndex(index);
  };

  const handlePhotoNavigate = (direction) => {
    const newIndex = currentPhotoIndex + direction;
    if (newIndex >= 0 && newIndex < photos.length) {
      setCurrentPhotoIndex(newIndex);
      setSelectedPhoto(photos[newIndex]);
    }
  };

  return (
    <div className="min-h-screen pt-24" data-testid="media-page">
      {/* Hero Section */}
      <section className="py-16">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto text-center"
          >
            <h1 className="text-4xl md:text-6xl font-serif font-bold text-white mb-6" data-testid="media-title">
              The Matrix University Media
            </h1>
            <p className="text-xl text-muted-foreground">
              Browse our collection of VR experiences, videos, and photos showcasing the immersive world of The Matrix University.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Tab Switcher */}
      <section className="py-4">
        <div className="container-custom">
          <div className="flex justify-center gap-4 mb-8">
            <button
              onClick={() => setActiveTab('photos')}
              className={`flex items-center gap-2 px-6 py-3 rounded-full font-semibold transition-all ${
                activeTab === 'photos'
                  ? 'bg-primary text-white glow-purple'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
              data-testid="tab-photos"
            >
              <ImageIcon className="w-5 h-5" />
              Photo Gallery ({photos.length})
            </button>
            <button
              onClick={() => setActiveTab('videos')}
              className={`flex items-center gap-2 px-6 py-3 rounded-full font-semibold transition-all ${
                activeTab === 'videos'
                  ? 'bg-primary text-white glow-purple'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
              data-testid="tab-videos"
            >
              <Video className="w-5 h-5" />
              Videos ({videos.length})
            </button>
          </div>
        </div>
      </section>

      {/* Content Based on Active Tab */}
      <AnimatePresence mode="wait">
        {activeTab === 'photos' ? (
          <motion.section
            key="photos"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="py-8 pb-24"
          >
            <div className="container-custom">
              {loading ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                  {[...Array(15)].map((_, i) => (
                    <div key={i} className="aspect-square rounded-xl glass-light animate-pulse bg-muted" />
                  ))}
                </div>
              ) : photos.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                  {photos.map((photo, index) => (
                    <PhotoCard
                      key={photo.id}
                      photo={photo}
                      onClick={() => handlePhotoClick(photo, index)}
                      index={index}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <p className="text-muted-foreground">No photos available yet.</p>
                </div>
              )}
            </div>
          </motion.section>
        ) : (
          <motion.section
            key="videos"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="py-8 pb-24"
          >
            <div className="container-custom">
              {/* Category Filter */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex flex-wrap items-center gap-4 mb-8"
              >
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Filter className="w-5 h-5" />
                  <span className="text-sm font-medium">Filter:</span>
                </div>
                
                <div className="flex flex-wrap gap-2">
                  {videoCategories.map((category) => (
                    <button
                      key={category}
                      onClick={() => setSelectedCategory(category)}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                        selectedCategory === category
                          ? 'bg-primary text-white'
                          : 'bg-muted text-muted-foreground hover:bg-muted/80'
                      }`}
                      data-testid={`category-${category}`}
                    >
                      {category === 'all' ? 'All Videos' : category.charAt(0).toUpperCase() + category.slice(1).replace(/-/g, ' ')}
                    </button>
                  ))}
                </div>
              </motion.div>

              {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <div key={i} className="rounded-xl glass-light animate-pulse">
                      <div className="aspect-video bg-muted" />
                      <div className="p-4 space-y-3">
                        <div className="h-5 bg-muted rounded" />
                        <div className="h-4 bg-muted rounded w-3/4" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : filteredVideos.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {filteredVideos.map((video, index) => (
                    <VideoCard
                      key={video.id}
                      video={video}
                      onClick={setSelectedVideo}
                      index={index}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <p className="text-muted-foreground">No videos found in this category.</p>
                </div>
              )}
            </div>
          </motion.section>
        )}
      </AnimatePresence>

      {/* Video Modal */}
      <VideoModal
        isOpen={!!selectedVideo}
        onClose={() => setSelectedVideo(null)}
        videoId={selectedVideo?.youtube_id}
        title={selectedVideo?.title}
      />

      {/* Photo Modal */}
      <PhotoModal
        isOpen={!!selectedPhoto}
        onClose={() => setSelectedPhoto(null)}
        photo={selectedPhoto}
        photos={photos}
        currentIndex={currentPhotoIndex}
        onNavigate={handlePhotoNavigate}
      />
    </div>
  );
}
